import { preservesProtectedTokens } from "./protected-tokens.js";
import { correctSpelling } from "../text/spelling-corrector.js";

/**
 * REVISÃO LINGUÍSTICA (pt-BR) — SAFE_LANGUAGE_CHANGE.
 *
 * Duas fontes, um motor só:
 *   ORTOGRAFIA  o corretor ortográfico que já existia no sistema
 *               (`domain/text/spelling-corrector.ts`, dicionário curado de
 *               inspeção industrial) — REAPROVEITADO, não duplicado.
 *   GRAMÁTICA   regras conservadoras daqui: concordância da voz passiva com
 *               artigo ou substantivo de gênero conhecido, oração coordenada
 *               na passiva, "houveram" impessoal, palavra repetida.
 *
 * Na dúvida, a regra NÃO dispara. Toda sugestão passa pela proteção de
 * valores: se mexer em número, TAG, norma ou código, é descartada.
 */
export interface LanguageSuggestion {
  readonly ruleId: string;
  readonly category: "ORTOGRAFIA" | "GRAMATICA";
  readonly originalText: string;
  readonly suggestedText: string;
  readonly explanation: string;
  /** Posição do trecho no texto revisado. */
  readonly offset?: number;
}

type Gender = "m" | "f";

/** Substantivos técnicos frequentes com gênero conhecido. Fora desta lista e sem artigo, nada é corrigido. */
const NOUN_GENDER: Readonly<Record<string, Gender>> = {
  inspeções: "f", medições: "f", soldas: "f", corrosões: "f", trincas: "f", indicações: "f", anomalias: "f",
  descontinuidades: "f", juntas: "f", chapas: "f", tubulações: "f", linhas: "f", verificações: "f", avaliações: "f",
  leituras: "f", falhas: "f", perdas: "f", deformações: "f", evidências: "f", manutenções: "f", atividades: "f",
  varreduras: "f", regiões: "f", áreas: "f", superfícies: "f", conexões: "f", válvulas: "f", peças: "f", fotos: "f",
  ensaios: "m", pontos: "m", reparos: "m", testes: "m", danos: "m", vazamentos: "m", serviços: "m", equipamentos: "m",
  componentes: "m", exames: "m", registros: "m", defeitos: "m", pites: "m", alvéolos: "m", desgastes: "m", bocais: "m",
  flanges: "m", suportes: "m", cordões: "m", trechos: "m", acessórios: "m", instrumentos: "m", dispositivos: "m"
};

const PARTICIPLE_STEMS = [
  "realizad", "executad", "encontrad", "verificad", "constatad", "identificad", "observad", "inspecionad",
  "efetuad", "medid", "detectad", "registrad", "evidenciad", "localizad", "analisad", "avaliad", "reparad",
  "substituíd", "instalad", "removid", "aplicad", "concluíd", "iniciad"
];
const STEM = `(${PARTICIPLE_STEMS.join("|")})`;
const WORD = "[A-Za-zÀ-ÿ]+";

const ending = (g: Gender, plural: boolean) => (g === "f" ? (plural ? "as" : "a") : (plural ? "os" : "o"));
const capitalizeLike = (model: string, word: string) => (model[0] && model[0] === model[0].toUpperCase() ? word[0]!.toUpperCase() + word.slice(1) : word);

interface Hit { start: number; end: number; replacement: string; ruleId: string; category: LanguageSuggestion["category"]; explanation: string }

/**
 * "Foi realizado as inspeções" → "Foram realizadas as inspeções";
 * "Foi constatado a presença" → "Foi constatada a presença".
 * Só o verbo muda — o artigo e o substantivo são o contexto.
 */
function passiveWithArticle(text: string): Hit[] {
  const out: Hit[] = [];
  const re = new RegExp(`\\b(foi|foram)\\s+${STEM}(o|a|os|as)(?=\\s+(os|as|o|a)\\s+(${WORD}))`, "gi");
  for (const m of text.matchAll(re)) {
    const [verb, aux, stem, end, article, noun] = m as unknown as [string, string, string, string, string, string];
    const a = article.toLowerCase();
    const g: Gender = a.startsWith("a") ? "f" : "m";
    const plural = a.endsWith("s");
    const wantAux = plural ? "foram" : "foi";
    const wantEnd = ending(g, plural);
    if (aux.toLowerCase() === wantAux && end.toLowerCase() === wantEnd) continue;
    out.push({ start: m.index!, end: m.index! + verb.length, replacement: `${capitalizeLike(aux, wantAux)} ${stem}${wantEnd}`, ruleId: "PT_PASSIVE_AGREEMENT", category: "GRAMATICA", explanation: `Concordância da voz passiva com "${article} ${noun}".` });
  }
  return out;
}

/** "Foi realizado inspeções" → "Foram realizadas inspeções" (substantivo de gênero conhecido). */
function passiveWithKnownNoun(text: string): Hit[] {
  const out: Hit[] = [];
  const re = new RegExp(`\\b(foi)\\s+${STEM}(o|a)(?=\\s+(${WORD}s)(?![A-Za-zÀ-ÿ]))`, "gi");
  for (const m of text.matchAll(re)) {
    const [verb, aux, stem, , noun] = m as unknown as [string, string, string, string, string];
    const g = NOUN_GENDER[noun.toLowerCase()];
    if (!g) continue;
    out.push({ start: m.index!, end: m.index! + verb.length, replacement: `${capitalizeLike(aux, "foram")} ${stem}${ending(g, true)}`, ruleId: "PT_PASSIVE_AGREEMENT", category: "GRAMATICA", explanation: `Concordância da voz passiva com o sujeito no plural ("${noun}").` });
  }
  return out;
}

/** "… e encontrado corrosões" → "… e foram encontradas corrosões". */
function coordinatedParticiple(text: string): Hit[] {
  const out: Hit[] = [];
  const re = new RegExp(`\\be\\s+${STEM}o(?=\\s+(${WORD}s)(?![A-Za-zÀ-ÿ]))`, "gi");
  for (const m of text.matchAll(re)) {
    const [verb, stem, noun] = m as unknown as [string, string, string];
    const g = NOUN_GENDER[noun.toLowerCase()];
    if (!g) continue;
    out.push({ start: m.index!, end: m.index! + verb.length, replacement: `e foram ${stem}${ending(g, true)}`, ruleId: "PT_COORDINATED_PASSIVE", category: "GRAMATICA", explanation: `Oração coordenada na voz passiva: auxiliar e concordância com "${noun}".` });
  }
  return out;
}

/** "Houveram vazamentos" → "Houve vazamentos" (haver impessoal). */
function impersonalHaver(text: string): Hit[] {
  return [...text.matchAll(/\bhouveram\b/gi)].map(m => ({ start: m.index!, end: m.index! + m[0].length, replacement: capitalizeLike(m[0], "houve"), ruleId: "PT_HAVER_IMPESSOAL", category: "GRAMATICA" as const, explanation: "O verbo haver no sentido de existir é impessoal (fica no singular)." }));
}

function duplicatedWord(text: string): Hit[] {
  return [...text.matchAll(/\b([A-Za-zÀ-ÿ]{2,})\s+\1(?![A-Za-zÀ-ÿ])/gi)].map(m => ({ start: m.index!, end: m.index! + m[0].length, replacement: m[1]!, ruleId: "PT_DUPLICATED_WORD", category: "ORTOGRAFIA" as const, explanation: "Palavra repetida." }));
}

/** Ortografia: o corretor oficial do sistema, mudança por mudança. */
function spelling(text: string): Hit[] {
  const KIND: Record<string, string> = { GRAFIA: "Grafia.", "ACENTUAÇÃO": "Acentuação.", "ESPAÇAMENTO": "Espaçamento.", "MAIÚSCULA": "Maiúscula no início da frase." };
  return correctSpelling(text).changes.map(c => ({ start: c.index, end: c.index + c.from.length, replacement: c.to, ruleId: `PT_SPELLING_${c.kind}`, category: "ORTOGRAFIA" as const, explanation: KIND[c.kind] ?? "Ortografia." }));
}

/** Da mais específica para a mais geral: a primeira a cobrir um trecho fica com ele. */
const RULES: ReadonlyArray<(text: string) => Hit[]> = [passiveWithArticle, passiveWithKnownNoun, coordinatedParticiple, impersonalHaver, duplicatedWord, spelling];

export function reviewLanguage(text: string): LanguageSuggestion[] {
  if (!text || !text.trim()) return [];
  const taken: Array<[number, number]> = [];
  const out: LanguageSuggestion[] = [];
  for (const rule of RULES) {
    for (const h of rule(text)) {
      const original = text.slice(h.start, h.end);
      if (!original || original === h.replacement) continue;
      if (!preservesProtectedTokens(original, h.replacement)) continue;
      if (taken.some(([a, b]) => h.start < b && a < h.end)) continue;
      taken.push([h.start, h.end]);
      out.push({ ruleId: h.ruleId, category: h.category, originalText: original, suggestedText: h.replacement, explanation: h.explanation, offset: h.start });
    }
  }
  return out.sort((a, b) => (a.offset ?? 0) - (b.offset ?? 0));
}

/** Aplica sugestões (do fim para o começo: uma não desloca a posição das anteriores). */
export function applyLanguageSuggestions(text: string, suggestions: readonly LanguageSuggestion[]): string {
  let out = text;
  for (const s of [...suggestions].sort((a, b) => (b.offset ?? -1) - (a.offset ?? -1))) out = replaceAt(out, s.originalText, s.suggestedText, s.offset ?? -1);
  return out;
}

/** Troca o trecho na posição gravada; se o texto mudou, na primeira ocorrência; se sumiu, devolve o texto intacto. */
export function replaceAt(text: string, original: string, replacement: string, offset: number): string {
  if (offset >= 0 && text.substr(offset, original.length) === original) return text.slice(0, offset) + replacement + text.slice(offset + original.length);
  const i = text.indexOf(original);
  return i < 0 ? text : text.slice(0, i) + replacement + text.slice(i + original.length);
}
